import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../../sidebar";
import Header from "../../Header";
import Footer from "../../footer";
import axios from "axios";
import API_URL, { buildImageUrl } from "../../../config/config";
import useToggleSidebar from "../../../components/togglesidebar";
import { formatMoney, useSiteCurrency } from "../../../lib/money";
import "../../../styles/provider-products.css";

const CATEGORY_OPTIONS = [
  { label: "Male", value: "male" },
  { label: "Female", value: "female" },
  { label: "Unisex", value: "unisex" },
];

const BULK_CATEGORY_OPTIONS = [
  { label: "Auto", value: "auto" },
  ...CATEGORY_OPTIONS,
];

const EMPTY_FORM = {
  product_name: "",
  category: "unisex",
  product_image: "",
  images: [],
  product_price: "",
  description: "",
  keywords: "",
  source_url: "",
};

const WORKSPACE_VIEW_META = {
  list: {
    kicker: "Product Library",
    title: "Provider Products",
    subtitle: "Review products first, then open the workflow you need without scrolling through a long workspace.",
  },
  new: {
    kicker: "Product Workspace",
    title: "Add New Product",
    subtitle: "Create a new product in a dedicated screen so the form stays focused and easier to manage.",
  },
  fetch: {
    kicker: "URL Fetch Workspace",
    title: "Add Product From URL",
    subtitle: "Fetch product details from a product page, review them, then save or move them into the manual form.",
  },
  import: {
    kicker: "Bulk Workspace",
    title: "Bulk Import Products",
    subtitle: "Preview products from a category page, fix anything needed, and import only the selected rows.",
  },
  edit: {
    kicker: "Edit Workspace",
    title: "Edit Product",
    subtitle: "Update an existing product with its current data already loaded, and optionally refresh details from its source URL.",
  },
};

const toSafeString = (value) => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
};

const normalizePriceText = (value) => {
  const str = toSafeString(value);
  const match = str.match(/(\d[\d,]*(?:\.\d{1,2})?)/);
  return match ? match[1].replace(/,/g, "") : "";
};

const normalizeImageList = (value) => {
  if (!Array.isArray(value)) return [];
  const seen = new Set();

  return value
    .map((item) => toSafeString(item).trim())
    .filter((item) => {
      if (!item) return false;
      const key = item.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const buildExtraImages = (primaryImage, images) => {
  const primary = toSafeString(primaryImage).trim().toLowerCase();
  return normalizeImageList(images).filter((image) => image.toLowerCase() !== primary);
};

const buildImagePayload = (primaryImage, images) => {
  return buildExtraImages(primaryImage, images);
};

const mergeImagesIntoGallery = (primaryImage, images, incomingImages) => {
  const currentPrimary = toSafeString(primaryImage).trim();
  const gallery = buildExtraImages(primaryImage, images);
  const uploads = normalizeImageList(incomingImages);

  if (!uploads.length) {
    return {
      primaryImage: currentPrimary,
      extraImages: gallery,
    };
  }

  if (!currentPrimary) {
    return {
      primaryImage: uploads[0],
      extraImages: buildExtraImages(uploads[0], [...gallery, ...uploads.slice(1)]),
    };
  }

  return {
    primaryImage: currentPrimary,
    extraImages: buildExtraImages(currentPrimary, [...gallery, ...uploads]),
  };
};

const createKeywordSuggestion = (value) => {
  const noise = new Set([
    "and",
    "the",
    "for",
    "with",
    "from",
    "your",
    "this",
    "that",
    "made",
    "high",
    "quality",
    "medical",
    "scrubs",
    "scrub",
    "top",
    "pant",
    "suit",
  ]);

  const words = toSafeString(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, "")
    .split(/\s+/)
    .filter((word) => word.length > 2 && !noise.has(word));

  return Array.from(new Set(words)).join(", ");
};

const normalizeFetchedProductData = (payload, fallbackCurrency) => {
  const fetchedPrimaryImage = toSafeString(
    payload?.image || payload?.images?.[0]
  ).trim();

  return {
    ...payload,
    image: fetchedPrimaryImage,
    images: buildExtraImages(fetchedPrimaryImage, payload?.images),
    price: normalizePriceText(payload?.price),
    currency: toSafeString(payload?.currency || fallbackCurrency).trim(),
    rating: toSafeString(payload?.rating || "").trim(),
  };
};

const CyclingStatusText = ({ active, steps = [], className, style }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [dots, setDots] = useState("");

  useEffect(() => {
    if (!active) {
      setStepIndex(0);
      setDots("");
      return undefined;
    }

    const stepTimer = setInterval(() => {
      setStepIndex((prev) => (steps.length ? (prev + 1) % steps.length : 0));
    }, 1400);

    const dotTimer = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? "" : `${prev}.`));
    }, 350);

    return () => {
      clearInterval(stepTimer);
      clearInterval(dotTimer);
    };
  }, [active, steps.length]);

  if (!active) return null;

  const label = steps.length ? steps[stepIndex] : "Working";
  return (
    <span className={className} style={style}>
      {label}
      {dots}
    </span>
  );
};

const ProductImagesManager = ({
  primaryImage,
  extraImages,
  errors = {},
  onPrimaryChange,
  onAddImage,
  onExtraImageChange,
  onRemoveImage,
  onMakePrimary,
  onUploadFiles,
  uploading = false,
}) => {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const totalImages = [primaryImage, ...extraImages].filter((image) => toSafeString(image).trim()).length;
  const hasImages = totalImages > 0;

  const handleFiles = async (fileList) => {
    if (!onUploadFiles) return;
    const files = Array.from(fileList || []).filter(
      (file) => file && typeof file.type === "string" && file.type.startsWith("image/")
    );
    if (!files.length) return;
    await onUploadFiles(files);
  };

  return (
    <div className="border rounded-3 p-3 p-md-4 bg-light-subtle">
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
        <div>
          <h6 className="mb-1">Product Images</h6>
          <small className="text-muted">
            Set one primary image and add more gallery images for the frontend carousel.
          </small>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          onClick={onAddImage}
        >
          <i className="fa fa-plus me-1" />
          Add image
        </button>
      </div>

      <div
        className={`border rounded-3 bg-white p-3 mb-3 ${dragActive ? "border-primary shadow-sm" : ""}`}
        onDragEnter={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(false);
        }}
        onDrop={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          setDragActive(false);
          await handleFiles(event.dataTransfer?.files);
        }}
        style={{ borderStyle: "dashed" }}
      >
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <div className="fw-semibold">Upload from device</div>
            <small className="text-muted">
              Drag and drop images here or select files. Uploaded images are stored with the same product image pipeline.
            </small>
          </div>
          <div className="d-flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={async (event) => {
                await handleFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <button
              type="button"
              className="btn btn-outline-dark btn-sm"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? "Uploading..." : "Upload images"}
            </button>
          </div>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-lg-5">
          <label className="form-label">Primary Image URL</label>
          <input
            type="text"
            className={`form-control ${errors.product_image ? "is-invalid" : ""}`}
            value={primaryImage}
            onChange={(e) => onPrimaryChange(e.target.value)}
            placeholder="Paste main product image URL"
          />
          {errors.product_image && (
            <div className="invalid-feedback">{errors.product_image}</div>
          )}

          <div className="mt-3 border rounded-3 bg-white d-flex align-items-center justify-content-center overflow-hidden" style={{ minHeight: 220 }}>
            {primaryImage ? (
              <img
                src={buildImageUrl(primaryImage)}
                alt="Primary preview"
                className="img-fluid"
                style={{ maxHeight: 220, objectFit: "contain" }}
              />
            ) : (
              <div className="text-muted text-center px-3">
                Primary image preview
              </div>
            )}
          </div>
        </div>

        <div className="col-lg-7">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <label className="form-label mb-0">Gallery Images</label>
            <small className="text-muted">
              {hasImages ? `${totalImages} image${totalImages > 1 ? "s" : ""} ready` : "No images added yet"}
            </small>
          </div>

          {extraImages.length ? (
            <div className="d-flex flex-column gap-3">
              {extraImages.map((image, index) => (
                <div key={`gallery-image-${index}`} className="border rounded-3 bg-white p-3">
                  <div className="row g-3 align-items-start">
                    <div className="col-md-8">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong className="small text-uppercase text-muted">
                          Image {index + 2}
                        </strong>
                        <div className="d-flex gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => onMakePrimary(index)}
                          >
                            Make Primary
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => onRemoveImage(index)}
                          >
                            <i className="fa fa-trash me-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        value={image}
                        onChange={(e) => onExtraImageChange(index, e.target.value)}
                        placeholder="Paste additional image URL"
                      />
                    </div>

                    <div className="col-md-4">
                      <div className="border rounded-3 bg-light d-flex align-items-center justify-content-center overflow-hidden" style={{ minHeight: 140 }}>
                        {image ? (
                          <img
                            src={buildImageUrl(image)}
                            alt={`Gallery preview ${index + 2}`}
                            className="img-fluid"
                            style={{ maxHeight: 140, objectFit: "contain" }}
                          />
                        ) : (
                          <div className="text-muted text-center px-2">
                            Image preview
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="border rounded-3 bg-white text-muted text-center py-4">
              No additional images. Click <strong>Add image</strong> to create a gallery.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProviderProducts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, productId } = useParams();
  const token = localStorage.getItem("adminToken");
  const authHeaders = useMemo(
    () =>
      token
        ? {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        : {},
    [token]
  );

  const currency = useSiteCurrency();

  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();
  const providerProductsPath = useMemo(
    () => `/admin/providers/products/${id}`,
    [id]
  );
  const workspacePaths = useMemo(
    () => ({
      list: providerProductsPath,
      new: `${providerProductsPath}/new`,
      fetch: `${providerProductsPath}/fetch`,
      import: `${providerProductsPath}/import`,
      edit: (nextProductId) => `${providerProductsPath}/edit/${nextProductId}`,
    }),
    [providerProductsPath]
  );
  const currentView = useMemo(() => {
    const pathname = location.pathname.replace(/\/+$/, "");
    if (pathname.endsWith("/new")) return "new";
    if (pathname.endsWith("/fetch")) return "fetch";
    if (pathname.endsWith("/import")) return "import";
    if (/\/edit\/[^/]+$/.test(pathname)) return "edit";
    return "list";
  }, [location.pathname]);
  const currentViewMeta = WORKSPACE_VIEW_META[currentView] || WORKSPACE_VIEW_META.list;

  const [providerName, setProviderName] = useState("");
  const [productUrl, setProductUrl] = useState("");
  const [productData, setProductData] = useState(null);
  const [fetchingProduct, setFetchingProduct] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [fetchStepActive, setFetchStepActive] = useState(false);

  const [bulkCollectionUrl, setBulkCollectionUrl] = useState("");
  const [bulkCategory, setBulkCategory] = useState("auto");
  const [bulkPreviewLoading, setBulkPreviewLoading] = useState(false);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkError, setBulkError] = useState("");
  const [bulkSuccess, setBulkSuccess] = useState("");
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkDetectedCategory, setBulkDetectedCategory] = useState("");
  const [bulkPreviewStepActive, setBulkPreviewStepActive] = useState(false);

  const [products, setProducts] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [tableError, setTableError] = useState("");

  const [productsPage, setProductsPage] = useState(1);
  const [productsTotal, setProductsTotal] = useState(0);
  const [productsHasMore, setProductsHasMore] = useState(true);
  const [loadingMoreProducts, setLoadingMoreProducts] = useState(false);
  const loadMoreRef = useRef(null);

  const [formData, setFormData] = useState(() => ({ ...EMPTY_FORM, images: [] }));
  const [formErrors, setFormErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [formSuccess, setFormSuccess] = useState("");
  const [formImageUploading, setFormImageUploading] = useState(false);
  const [previewImageUploading, setPreviewImageUploading] = useState(false);
  const [formFetchLoading, setFormFetchLoading] = useState(false);
  const [formFetchError, setFormFetchError] = useState("");
  const [pendingDeleteProduct, setPendingDeleteProduct] = useState(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editLoadError, setEditLoadError] = useState("");
  const [listFlashMessage, setListFlashMessage] = useState("");

  const updateFormExtraImage = useCallback((index, value) => {
    setFormErrors((prev) => ({ ...prev, images: undefined }));
    setFormData((prev) => {
      const nextImages = [...(Array.isArray(prev.images) ? prev.images : [])];
      nextImages[index] = value;
      return { ...prev, images: nextImages };
    });
  }, []);

  const addFormExtraImage = useCallback(() => {
    setFormErrors((prev) => ({ ...prev, images: undefined }));
    setFormData((prev) => ({
      ...prev,
      images: [...(Array.isArray(prev.images) ? prev.images : []), ""],
    }));
  }, []);

  const removeFormExtraImage = useCallback((index) => {
    setFormErrors((prev) => ({ ...prev, images: undefined }));
    setFormData((prev) => ({
      ...prev,
      images: buildExtraImages(
        prev.product_image,
        (Array.isArray(prev.images) ? prev.images : []).filter((_, imageIndex) => imageIndex !== index)
      ),
    }));
  }, []);

  const makeFormImagePrimary = useCallback((index) => {
    setFormErrors((prev) => ({ ...prev, product_image: undefined, images: undefined }));
    setFormData((prev) => {
      const extraImages = buildExtraImages(prev.product_image, prev.images);
      const nextPrimary = toSafeString(extraImages[index]).trim();
      if (!nextPrimary) return prev;

      const remainingImages = extraImages.filter((_, imageIndex) => imageIndex !== index);
      const currentPrimary = toSafeString(prev.product_image).trim();

      return {
        ...prev,
        product_image: nextPrimary,
        images: currentPrimary ? [currentPrimary, ...remainingImages] : remainingImages,
      };
    });
  }, []);

  const updatePreviewExtraImage = useCallback((index, value) => {
    setProductData((prev) => {
      if (!prev) return prev;
      const nextImages = [...(Array.isArray(prev.images) ? prev.images : [])];
      nextImages[index] = value;
      return { ...prev, images: nextImages };
    });
  }, []);

  const addPreviewExtraImage = useCallback(() => {
    setProductData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        images: [...(Array.isArray(prev.images) ? prev.images : []), ""],
      };
    });
  }, []);

  const removePreviewExtraImage = useCallback((index) => {
    setProductData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        images: buildExtraImages(
          prev.image,
          (Array.isArray(prev.images) ? prev.images : []).filter((_, imageIndex) => imageIndex !== index)
        ),
      };
    });
  }, []);

  const makePreviewImagePrimary = useCallback((index) => {
    setProductData((prev) => {
      if (!prev) return prev;
      const extraImages = buildExtraImages(prev.image, prev.images);
      const nextPrimary = toSafeString(extraImages[index]).trim();
      if (!nextPrimary) return prev;

      const remainingImages = extraImages.filter((_, imageIndex) => imageIndex !== index);
      const currentPrimary = toSafeString(prev.image).trim();

      return {
        ...prev,
        image: nextPrimary,
        images: currentPrimary ? [currentPrimary, ...remainingImages] : remainingImages,
      };
    });
  }, []);

  const uploadImagesToServer = useCallback(
    async (files) => {
      if (!id) return [];

      const payload = new FormData();
      files.forEach((file) => payload.append("images", file));

      const response = await axios.post(
        `${API_URL}/providers/${id}/products/upload-images`,
        payload,
        {
          headers: {
            ...(authHeaders.headers || {}),
          },
        }
      );

      return normalizeImageList(
        Array.isArray(response.data?.items)
          ? response.data.items.map((item) => item?.original)
          : []
      );
    },
    [authHeaders.headers, id]
  );

  const handleFormImageUpload = useCallback(
    async (files) => {
      try {
        setFormImageUploading(true);
        setFormErrors((prev) => ({ ...prev, api: undefined, product_image: undefined, images: undefined }));
        const uploadedImages = await uploadImagesToServer(files);
        if (!uploadedImages.length) return;

        setFormData((prev) => {
          const merged = mergeImagesIntoGallery(prev.product_image, prev.images, uploadedImages);
          return {
            ...prev,
            product_image: merged.primaryImage,
            images: merged.extraImages,
          };
        });
      } catch (error) {
        console.error("Form image upload failed:", error);
        setFormErrors((prev) => ({
          ...prev,
          api: error.response?.data?.message || "Failed to upload product images.",
        }));
      } finally {
        setFormImageUploading(false);
      }
    },
    [uploadImagesToServer]
  );

  const handlePreviewImageUpload = useCallback(
    async (files) => {
      try {
        setPreviewImageUploading(true);
        const uploadedImages = await uploadImagesToServer(files);
        if (!uploadedImages.length) return;

        setProductData((prev) => {
          if (!prev) return prev;
          const merged = mergeImagesIntoGallery(prev.image, prev.images, uploadedImages);
          return {
            ...prev,
            image: merged.primaryImage,
            images: merged.extraImages,
          };
        });
      } catch (error) {
        console.error("Preview image upload failed:", error);
        setFetchError(error.response?.data?.message || "Failed to upload product images.");
      } finally {
        setPreviewImageUploading(false);
      }
    },
    [uploadImagesToServer]
  );

  useEffect(() => {
    const fetchProvider = async () => {
      if (!id) return;
      try {
        const res = await axios.get(`${API_URL}/providers/${id}`, authHeaders);
        setProviderName(res.data?.item?.name || "");
      } catch (err) {
        console.error("Failed to fetch provider:", err);
      }
    };

    fetchProvider();
  }, [id, authHeaders]);

  const fetchProducts = useCallback(async () => {
    if (!id) return;
    try {
      setTableLoading(true);
      setTableError("");
      const res = await axios.get(
        `${API_URL}/providers/${id}/products`,
        {
          ...authHeaders,
          params: { page: 1, limit: 10 },
        }
      );

      const list = res.data?.products || [];
      const total = Number(res.data?.total || list.length || 0);
      const limit = Number(res.data?.limit || 10);
      const page = Number(res.data?.page || 1);
      const totalPages = Number(res.data?.totalPages || Math.ceil(total / limit) || 1);

      setProducts(list);
      setProductsTotal(total);
      setProductsPage(page);
      setProductsHasMore(page < totalPages);
    } catch (err) {
      console.error("Failed to load products:", err);
      setTableError(
        err.response?.data?.message || "Unable to load provider products."
      );
    } finally {
      setTableLoading(false);
    }
  }, [id, authHeaders]);

  const loadMoreProducts = useCallback(async () => {
    if (!id) return;
    if (loadingMoreProducts || tableLoading) return;
    if (!productsHasMore) return;

    const nextPage = productsPage + 1;

    try {
      setLoadingMoreProducts(true);
      const res = await axios.get(
        `${API_URL}/providers/${id}/products`,
        {
          ...authHeaders,
          params: { page: nextPage, limit: 10 },
        }
      );

      const list = res.data?.products || [];
      const total = Number(res.data?.total || productsTotal);
      const limit = Number(res.data?.limit || 10);
      const page = Number(res.data?.page || nextPage);
      const totalPages = Number(res.data?.totalPages || Math.ceil(total / limit) || 1);

      setProducts((prev) => [...prev, ...list]);
      setProductsTotal(total);
      setProductsPage(page);
      setProductsHasMore(page < totalPages);
    } catch (err) {
      console.error("Load more products failed:", err);
    } finally {
      setLoadingMoreProducts(false);
    }
  }, [
    id,
    authHeaders,
    loadingMoreProducts,
    tableLoading,
    productsHasMore,
    productsPage,
    productsTotal,
  ]);

  const normalizeRowPrice = (value) => {
    const raw = toSafeString(value);
    const numeric = raw.replace(/[^0-9.]/g, "");
    return numeric;
  };

  const isBulkRowValid = (row) => {
    const name = toSafeString(row?.product_name).trim();
    const image = toSafeString(row?.product_image).trim();
    const price = Number(toSafeString(row?.product_price).replace(/[^0-9.]/g, ""));
    return Boolean(name && image && price && !Number.isNaN(price) && price > 0);
  };

  const handleBulkPreview = async () => {
    const url = bulkCollectionUrl.trim();
    if (!url) {
      setBulkError("Please paste category page link.");
      return;
    }

    try {
      setBulkPreviewStepActive(true);
      setBulkPreviewLoading(true);
      setBulkError("");
      setBulkSuccess("");
      setBulkItems([]);

      const res = await axios.post(
        `${API_URL}/providers/${id}/products/import-preview`,
        {
          url,
          category: bulkCategory,
          maxProducts: 300,
          maxPages: 20,
        },
        authHeaders
      );

      setBulkDetectedCategory(toSafeString(res.data?.detectedCategory).trim());

      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      setBulkItems(
        items.map((item) => ({
          ...item,
          selected: true,
          product_price: normalizeRowPrice(item.product_price),
        }))
      );
    } catch (err) {
      console.error("Bulk preview error:", err);
      setBulkError(
        err.response?.data?.message || "Failed to preview collection products."
      );
    } finally {
      setBulkPreviewLoading(false);
      setBulkPreviewStepActive(false);
    }
  };

  const handleBulkRowChange = (tempId, field, value) => {
    setBulkItems((prev) =>
      prev.map((row) => {
        if (row.tempId !== tempId) return row;
        if (field === "product_price") {
          return { ...row, product_price: normalizeRowPrice(value) };
        }
        return { ...row, [field]: value };
      })
    );
  };

  const toggleBulkRowSelected = (tempId) => {
    setBulkItems((prev) =>
      prev.map((row) =>
        row.tempId === tempId ? { ...row, selected: !row.selected } : row
      )
    );
  };

  const toggleBulkSelectAll = () => {
    setBulkItems((prev) => {
      const allSelected = prev.length && prev.every((row) => row.selected);
      return prev.map((row) => ({ ...row, selected: !allSelected }));
    });
  };

  const removeBulkRow = (tempId) => {
    setBulkItems((prev) => prev.filter((row) => row.tempId !== tempId));
  };

  const handleBulkImport = async () => {
    const selected = bulkItems.filter((row) => row.selected);
    if (!selected.length) {
      setBulkError("Select at least one product to import.");
      return;
    }

    const invalid = selected.filter((row) => !isBulkRowValid(row));
    if (invalid.length) {
      setBulkError(
        `Some selected rows are invalid. Please fix name/image/price. (Invalid: ${invalid.length})`
      );
      return;
    }

    try {
      setBulkImporting(true);
      setBulkError("");
      setBulkSuccess("");

      const payloadItems = selected.map((row) => ({
        product_name: toSafeString(row.product_name).trim(),
        product_image: toSafeString(row.product_image).trim(),
        images: buildImagePayload(row.product_image, row.images),
        product_price: Number(
          toSafeString(row.product_price).replace(/[^0-9.]/g, "")
        ),
        category: row.category || bulkCategory || "unisex",
        source_url: toSafeString(row.source_url).trim(),
        description: toSafeString(row.description || "").trim(),
        keywords: toSafeString(row.keywords || "").trim(),
      }));

      const res = await axios.post(
        `${API_URL}/providers/${id}/products/import-bulk`,
        { items: payloadItems },
        authHeaders
      );

      setBulkSuccess(
        res.data?.message || "Bulk import completed successfully."
      );
      setBulkItems([]);
      setBulkCollectionUrl("");
      fetchProducts();
    } catch (err) {
      console.error("Bulk import error:", err);
      setBulkError(
        err.response?.data?.message || "Bulk import failed. Please try again."
      );
    } finally {
      setBulkImporting(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          loadMoreProducts();
        }
      },
      { root: null, rootMargin: "250px", threshold: 0.01 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMoreProducts]);

  const fetchProductFromUrl = useCallback(
    async (rawUrl) => {
      const nextUrl = toSafeString(rawUrl).trim();
      if (!nextUrl) {
        const error = new Error("Please paste product page link.");
        error.isUserFacing = true;
        throw error;
      }

      const res = await axios.get(`${API_URL}/providers/fetch-product`, {
        ...authHeaders,
        params: { url: nextUrl },
      });

      if (!res.data?.success) {
        const error = new Error("No data found for the provided URL.");
        error.isUserFacing = true;
        throw error;
      }

      return normalizeFetchedProductData(res.data, currency.code);
    },
    [authHeaders, currency.code]
  );

  const mergeFetchedDataIntoForm = useCallback((fetched) => {
    setFormData((prev) => {
      const nextPrimaryImage = toSafeString(
        fetched?.image || fetched?.images?.[0] || prev.product_image
      ).trim();
      const suggestedCategory = toSafeString(fetched?.suggestedCategory).trim();

      return {
        ...prev,
        product_name: toSafeString(fetched?.name || prev.product_name),
        category:
          toSafeString(prev.category || "unisex") === "unisex" &&
          suggestedCategory &&
          suggestedCategory !== "unisex"
            ? suggestedCategory
            : prev.category,
        product_image: nextPrimaryImage,
        images: buildExtraImages(nextPrimaryImage, fetched?.images),
        product_price: toSafeString(fetched?.price || prev.product_price),
        description: toSafeString(fetched?.description || prev.description || ""),
        keywords: toSafeString(fetched?.keywords || prev.keywords || ""),
        source_url: toSafeString(fetched?.url || prev.source_url || ""),
      };
    });
    setFormErrors({});
    setFormFetchError("");
  }, []);

  const handleFetchProduct = async () => {
    try {
      setFetchStepActive(true);
      setFetchingProduct(true);
      setFetchError("");
      setProductData(null);
      const fetched = await fetchProductFromUrl(productUrl);
      setProductData(fetched);
    } catch (err) {
      console.error("FETCH ERROR:", err.response?.data || err.message);
      setFetchError(
        err?.message || err.response?.data?.message || "Failed to fetch product details."
      );
    } finally {
      setFetchingProduct(false);
      setFetchStepActive(false);
    }
  };

  const handleFetchIntoForm = async () => {
    try {
      setFormFetchLoading(true);
      setFormFetchError("");
      const fetched = await fetchProductFromUrl(formData.source_url || productUrl);
      setProductData(fetched);
      mergeFetchedDataIntoForm(fetched);
      setFormSuccess(
        editingProductId
          ? "Product details refreshed from URL. Review and save the update."
          : "Product details fetched into the form. Review and save."
      );
    } catch (err) {
      console.error("FETCH INTO FORM ERROR:", err.response?.data || err.message);
      setFormFetchError(
        err?.message || err.response?.data?.message || "Failed to fetch product details into the form."
      );
    } finally {
      setFormFetchLoading(false);
    }
  };

  const applyScrapedData = () => {
    if (!productData) return;
    mergeFetchedDataIntoForm(productData);
    if (currentView !== "edit") {
      setEditingProductId(null);
    }
    setFormSuccess(
      currentView === "edit"
        ? "Fetched details copied into the edit form."
        : "Fetched details copied into the manual form."
    );
    navigate(
      currentView === "edit" && editingProductId
        ? workspacePaths.edit(editingProductId)
        : workspacePaths.new,
      currentView === "edit" && editingProductId
        ? undefined
        : { state: { preserveForm: true } }
    );
  };

  const handleImportFromUrl = async () => {
    if (!productData) {
      alert("Fetch a product first.");
      return;
    }

    const importName = toSafeString(productData.name).trim();
    const importImage = toSafeString(
      productData.image || productData.images?.[0]
    ).trim();
    const importPrice = Number(
      toSafeString(productData.price).replace(/[^0-9.]/g, "")
    );

    const suggestedCategory = toSafeString(productData.suggestedCategory).trim();
    const importCategory =
      (toSafeString(formData.category || "unisex") === "unisex" &&
        suggestedCategory &&
        suggestedCategory !== "unisex")
        ? suggestedCategory
        : (formData.category || "unisex");

    const sourceUrl = toSafeString(productData.url || productUrl).trim();

    const payload = {
      product_name: importName,
      category: importCategory,
      product_image: importImage,
      images: buildImagePayload(importImage, productData.images),
      product_price: importPrice,
      source_url: sourceUrl,
      provider_id: id,
      description: toSafeString(productData.description || "").trim(),
      keywords: toSafeString(productData.keywords || "").trim(),
    };

    if (!importName || !importImage) {
      alert("Fetched data missing name or image. Please complete the form manually.");
      return;
    }

    if (!importPrice || Number.isNaN(importPrice) || importPrice <= 0) {
      alert("Fetched price invalid. Please adjust price manually.");
      return;
    }

    try {
      setFormSubmitting(true);
      const response = await axios.post(
        `${API_URL}/providers/${id}/products`,
        payload,
        authHeaders
      );
      const successMessage =
        response.data?.message || "Product created from URL.";
      setProductUrl("");
      setProductData(null);
      fetchProducts();
      navigate(workspacePaths.list, {
        state: { flashSuccess: successMessage },
      });
    } catch (err) {
      console.error("Import from URL failed:", err);
      alert(err.response?.data?.message || "Failed to save product from URL.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM, images: [] });
    setFormErrors({});
    setFormSuccess("");
    setEditingProductId(null);
    setFormFetchError("");
    setEditLoadError("");
  };

  const populateFormFromProduct = useCallback((product, options = {}) => {
    const { preserveSuccess = false } = options;
    setEditingProductId(product?._id || null);
    setFormData({
      product_name: toSafeString(product?.product_name),
      category: product?.category || "unisex",
      product_image: toSafeString(product?.product_image),
      images: buildExtraImages(product?.product_image, product?.images),
      product_price: toSafeString(product?.product_price ?? ""),
      description: toSafeString(product?.description),
      keywords: toSafeString(product?.keywords || ""),
      source_url: toSafeString(product?.source_url || ""),
    });
    setFormErrors({});
    setFormFetchError("");
    setEditLoadError("");
    if (!preserveSuccess) {
      setFormSuccess("");
    }
  }, []);

  const loadProductForEdit = useCallback(async () => {
    if (currentView !== "edit" || !id || !productId) return;

    try {
      setEditLoading(true);
      setEditLoadError("");
      const res = await axios.get(
        `${API_URL}/providers/${id}/products/${productId}`,
        authHeaders
      );
      populateFormFromProduct(res.data?.product || {});
    } catch (err) {
      console.error("Failed to load product for edit:", err);
      setEditLoadError(
        err.response?.data?.message || "Unable to load product details for editing."
      );
    } finally {
      setEditLoading(false);
    }
  }, [authHeaders, currentView, id, productId, populateFormFromProduct]);

  useEffect(() => {
    if (currentView === "new" && !location.state?.preserveForm) {
      resetForm();
      setProductData(null);
      setProductUrl("");
    }
  }, [currentView, location.state]);

  useEffect(() => {
    if (currentView !== "list") return;
    const flashSuccess = toSafeString(location.state?.flashSuccess).trim();
    if (!flashSuccess) return;

    setListFlashMessage(flashSuccess);
    navigate(providerProductsPath, { replace: true });
  }, [currentView, location.state, navigate, providerProductsPath]);

  useEffect(() => {
    loadProductForEdit();
  }, [loadProductForEdit]);

  const validateForm = () => {
    const errors = {};
    const name = toSafeString(formData.product_name).trim();
    const image = toSafeString(formData.product_image).trim();
    const extraImages = buildExtraImages(formData.product_image, formData.images);
    const price = Number(
      toSafeString(formData.product_price).replace(/[^0-9.]/g, "")
    );

    if (!name) {
      errors.product_name = "Product name is required.";
    }
    if (!image) {
      errors.product_image = "Product image URL is required.";
    }
    if (!price || Number.isNaN(price) || price <= 0) {
      errors.product_price = "Enter a valid price.";
    }
    if (!CATEGORY_OPTIONS.some((c) => c.value === formData.category)) {
      errors.category = "Invalid category selected.";
    }
    if (extraImages.some((entry) => !toSafeString(entry).trim())) {
      errors.images = "Additional image URLs cannot be empty.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleKeywordsKeyDown = (e, target = "form") => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const val = e.target.value;
      if (!val.trim()) return;

      // Ensure trailing comma and space if enter was pressed
      const current = val.trim();
      const suffix = current.endsWith(",") ? " " : ", ";
      const newValue = current + suffix;

      if (target === "form") {
        setFormData(prev => ({ ...prev, keywords: newValue }));
      } else {
        setProductData(prev => ({ ...prev, keywords: newValue }));
      }
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "product_price" ? value.replace(/[^0-9.]/g, "") : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormSuccess("");
    if (!validateForm()) return;

    const name = toSafeString(formData.product_name).trim();
    const image = toSafeString(formData.product_image).trim();
    const price = Number(
      toSafeString(formData.product_price).replace(/[^0-9.]/g, "")
    );

    const payload = {
      ...formData,
      product_name: name,
      product_image: image,
      images: buildImagePayload(image, formData.images),
      product_price: price,
      provider_id: id,
    };

    try {
      setFormSubmitting(true);
      let response;
      const isEditing = Boolean(editingProductId);

      if (isEditing) {
        response = await axios.patch(
          `${API_URL}/providers/${id}/products/${editingProductId}`,
          payload,
          authHeaders
        );
      } else {
        response = await axios.post(
          `${API_URL}/providers/${id}/products`,
          payload,
          authHeaders
        );
      }

      const successMessage = response.data?.message || "Product saved successfully.";
      fetchProducts();
      if (isEditing) {
        populateFormFromProduct(response.data?.product || payload, {
          preserveSuccess: true,
        });
        navigate(workspacePaths.list, {
          state: { flashSuccess: successMessage },
        });
      } else {
        resetForm();
        navigate(workspacePaths.list, {
          state: { flashSuccess: successMessage },
        });
      }
    } catch (err) {
      console.error("Product save failed:", err);
      setFormErrors((prev) => ({
        ...prev,
        api:
          err.response?.data?.message ||
          "Something went wrong while saving product.",
      }));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    if (!product?._id) return;
    navigate(workspacePaths.edit(product._id));
  };

  const handleDelete = (product) => {
    setDeleteError("");
    setPendingDeleteProduct(product);
  };

  const handleCancelDelete = () => {
    if (deleteSubmitting) return;
    setDeleteError("");
    setPendingDeleteProduct(null);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteProduct?._id) return;
    try {
      setDeleteSubmitting(true);
      setDeleteError("");
      await axios.delete(
        `${API_URL}/providers/${id}/products/${pendingDeleteProduct._id}`,
        authHeaders
      );
      if (editingProductId === pendingDeleteProduct._id) {
        resetForm();
        navigate(workspacePaths.list);
      }
      setPendingDeleteProduct(null);
      fetchProducts();
    } catch (err) {
      console.error("Delete failed:", err);
      setDeleteError(
        err.response?.data?.message || "Failed to delete product."
      );
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const renderProductsTable = () => {
    const totalCount = productsTotal || products.length;
    const linkedCount = products.filter((product) =>
      toSafeString(product.source_url).trim()
    ).length;

    return (
      <div className="card border-0 shadow-sm mb-4 provider-products-surface">
        <div className="card-header bg-white border-0 pt-4 px-4 pb-3">
          <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-3">
            <div>
              <div className="provider-products-section-kicker">Product Library</div>
              <h4 className="mb-1">Products ({totalCount})</h4>
              <small className="text-muted">
                Start here. Review products first, then open the workflow you need.
              </small>
            </div>

            <div className="provider-products-toolbar provider-products-toolbar-tight">
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={fetchProducts} disabled={tableLoading}>
                <i className="fa fa-refresh me-1" />
                {tableLoading ? "Refreshing..." : "Refresh"}
              </button>
              <Link
                to={workspacePaths.new}
                className="btn btn-primary btn-sm"
              >
                <i className="fa fa-plus me-1" />
                New Product
              </Link>
              <Link to={workspacePaths.fetch} className="btn btn-outline-secondary btn-sm">
                <i className="fa fa-search me-1" />
                Fetch by URL
              </Link>
              <Link to={workspacePaths.import} className="btn btn-outline-dark btn-sm">
                <i className="fa fa-upload me-1" />
                Bulk Import
              </Link>
            </div>
          </div>

          <div className="provider-products-overview-strip mt-3">
            <div className="provider-products-overview-card">
              <span className="provider-products-stat-label">Loaded Now</span>
              <strong>{products.length}</strong>
              <small>Rows currently visible in this table.</small>
            </div>
            <div className="provider-products-overview-card">
              <span className="provider-products-stat-label">Source Linked</span>
              <strong>{linkedCount}</strong>
              <small>Visible products that still keep a source URL.</small>
            </div>
            <div className="provider-products-overview-card">
              <span className="provider-products-stat-label">Scroll Status</span>
              <strong>{productsHasMore ? "More Available" : "All Loaded"}</strong>
              <small>Infinite scroll stays active while more products exist.</small>
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          {listFlashMessage ? (
            <div className="alert alert-success m-4 mb-0 d-flex justify-content-between align-items-start gap-3">
              <span>{listFlashMessage}</span>
              <button
                type="button"
                className="btn-close"
                aria-label="Close success message"
                onClick={() => setListFlashMessage("")}
              />
            </div>
          ) : null}
          {tableError ? <div className="alert alert-danger m-4 mb-0">{tableError}</div> : null}

          <div className="px-4 pt-3 text-muted small">
            Newest products appear first. Open any workflow in its own page so the list stays clean and easier to manage.
          </div>

          <div className="table-responsive mt-3">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ minWidth: 280 }}>Product</th>
                  <th style={{ width: 130 }}>Category</th>
                  <th style={{ width: 160 }}>Price</th>
                  <th style={{ width: 140 }}>Created</th>
                  <th style={{ minWidth: 220 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-5">Loading products...</td>
                  </tr>
                ) : products.length ? (
                  products.map((product) => {
                    const hasSourceLink = Boolean(toSafeString(product.source_url).trim());

                    return (
                      <tr key={product._id}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            {product.product_image ? (
                              <img src={buildImageUrl(product.product_image)} alt={product.product_name} loading="lazy" className="provider-products-table-image" />
                            ) : (
                              <div className="provider-products-table-placeholder">N/A</div>
                            )}
                            <div>
                              <div className="fw-semibold">{product.product_name}</div>
                              <div className="text-muted small">#{product._id.slice(-6)}</div>
                              <div className="d-flex flex-wrap gap-2 mt-2">
                                <span className="badge rounded-pill text-bg-light text-capitalize">{product.category}</span>
                                <span className={`badge rounded-pill ${hasSourceLink ? "bg-success-subtle text-success-emphasis" : "bg-secondary-subtle text-secondary-emphasis"}`}>
                                  {hasSourceLink ? "Source linked" : "Manual entry"}
                                </span>
                              </div>
                              {hasSourceLink ? (
                                <a href={product.source_url} target="_blank" rel="noreferrer" className="small d-inline-block mt-2">
                                  Open source page
                                </a>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="text-capitalize">{product.category}</td>
                        <td>{formatMoney(product.product_price, currency)}</td>
                        <td>{new Date(product.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => handleEdit(product)}>
                              Edit
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(product)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="text-center py-5 text-muted">
                      No products found yet. Use <strong>New Product</strong>, <strong>Fetch by URL</strong>, or <strong>Bulk Import</strong> to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div ref={loadMoreRef} style={{ height: 1 }} />

          {productsHasMore && !tableLoading ? (
            <div className="text-center py-3 text-muted">{loadingMoreProducts ? "Loading more..." : "Scroll to load more"}</div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderManualWorkspace = () => {
    const isEditing = Boolean(editingProductId);

    return (
      <div id="provider-product-form" className="provider-products-section-card">
        <div className="provider-products-mode-banner mb-4">
          <div>
            <div className="provider-products-section-kicker">{isEditing ? "Edit Mode" : "Manual Workspace"}</div>
            <h5 className="mb-1">{isEditing ? "Edit Product" : "Add New Product"}</h5>
            <small className="text-muted">
              {isEditing
                ? "Update the selected product and optionally refetch fresh details from its source URL before saving."
                : "Create a product manually or start with a source URL to prefill the form before saving."}
            </small>
          </div>

          <div className="provider-products-inline-badges">
            <span className="badge rounded-pill text-bg-light">
              {isEditing ? `Editing #${editingProductId.slice(-6)}` : "Ready for new product"}
            </span>
            <span className="badge rounded-pill text-bg-light">
              {formData.source_url ? "Source URL attached" : "Source URL optional"}
            </span>
            {isEditing ? (
              <Link to={workspacePaths.list} className="btn btn-outline-secondary btn-sm">
                Back to Products
              </Link>
            ) : null}
          </div>
        </div>

        <form onSubmit={handleFormSubmit}>
          <div className="provider-products-form-panel provider-products-form-panel-accent mb-4">
            <div className="row g-3 align-items-end">
              <div className="col-lg-8">
                <label className="form-label">Source URL / Product Page</label>
                <input
                  type="text"
                  className="form-control"
                  name="source_url"
                  value={formData.source_url || ""}
                  onChange={handleFormChange}
                  placeholder="Paste the product page URL if you want to fetch details into this form"
                />
                <small className="text-muted">
                  Paste the latest product URL, then fetch to refresh name, description, images, price, and keywords directly into this form.
                </small>
              </div>
              <div className="col-lg-4">
                <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                  <button type="button" className="btn btn-outline-dark" onClick={handleFetchIntoForm} disabled={formFetchLoading}>
                    {formFetchLoading ? "Fetching..." : "Fetch Into Form"}
                  </button>
                  {formData.source_url ? (
                    <a className="btn btn-outline-secondary" href={formData.source_url} target="_blank" rel="noreferrer">
                      Open URL
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
            {formFetchError ? <div className="alert alert-danger mt-3 mb-0">{formFetchError}</div> : null}
          </div>

          <div className="row">
            <div className="col-lg-6 mb-3">
              <label className="form-label">Product Name</label>
              <input type="text" className={`form-control ${formErrors.product_name ? "is-invalid" : ""}`} name="product_name" value={formData.product_name} onChange={handleFormChange} />
              {formErrors.product_name ? <div className="invalid-feedback">{formErrors.product_name}</div> : null}
            </div>

            <div className="col-lg-3 mb-3">
              <label className="form-label">Category</label>
              <select className={`form-control ${formErrors.category ? "is-invalid" : ""}`} name="category" value={formData.category} onChange={handleFormChange}>
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {formErrors.category ? <div className="invalid-feedback">{formErrors.category}</div> : null}
            </div>

            <div className="col-lg-3 mb-3">
              <label className="form-label">Price ({currency.code})</label>
              <input type="text" className={`form-control ${formErrors.product_price ? "is-invalid" : ""}`} name="product_price" value={formData.product_price} onChange={handleFormChange} />
              {formErrors.product_price ? <div className="invalid-feedback">{formErrors.product_price}</div> : null}
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">Meta Description (Product Description)</label>
              <textarea className="form-control" rows="3" name="description" value={formData.description || ""} onChange={handleFormChange} placeholder="Enter a clear product description. This will be used as the meta description and shown on the product page." />
              <small className="text-muted">
                Recommended: 120-160 characters. Shown as the meta description for SEO and as the product description on the frontend.
              </small>
            </div>
          </div>

          <div className="row">
            <div className="col-12 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1 gap-2 flex-wrap">
                <label className="form-label mb-0">Meta Keywords</label>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none"
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      keywords: createKeywordSuggestion(prev.product_name),
                    }));
                  }}
                >
                  <i className="fa fa-magic me-1" />
                  Auto-generate
                </button>
              </div>
              <input type="text" className="form-control" name="keywords" value={formData.keywords || ""} onChange={handleFormChange} onKeyDown={(e) => handleKeywordsKeyDown(e, "form")} placeholder="e.g. medical scrubs, nursing uniform (Press Enter or Comma to separate)" />
              <small className="text-muted">
                Keywords are used as meta keywords and image alt text. Press Enter or Comma after each keyword.
              </small>
            </div>
          </div>

          <div className="mb-3">
            <ProductImagesManager
              primaryImage={toSafeString(formData.product_image)}
              extraImages={Array.isArray(formData.images) ? formData.images : []}
              errors={formErrors}
              onPrimaryChange={(value) => {
                setFormErrors((prev) => ({ ...prev, product_image: undefined, images: undefined }));
                setFormData((prev) => ({ ...prev, product_image: value, images: buildExtraImages(value, prev.images) }));
              }}
              onAddImage={addFormExtraImage}
              onExtraImageChange={updateFormExtraImage}
              onRemoveImage={removeFormExtraImage}
              onMakePrimary={makeFormImagePrimary}
              onUploadFiles={handleFormImageUpload}
              uploading={formImageUploading}
            />
            {formErrors.images ? <div className="text-danger small mt-2">{formErrors.images}</div> : null}
          </div>

          {formErrors.api ? <div className="alert alert-danger">{formErrors.api}</div> : null}
          {formSuccess ? <div className="alert alert-success">{formSuccess}</div> : null}

          <div className="provider-products-action-bar">
            <div className="text-muted small">Save only after checking price, images, description, and source URL.</div>
            <div className="d-flex flex-wrap gap-2">
              <button type="submit" className="btn btn-primary" disabled={formSubmitting}>
                {formSubmitting ? "Saving..." : isEditing ? "Update Product" : "Create Product"}
              </button>
              {isEditing ? (
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={loadProductForEdit}
                  disabled={formSubmitting || editLoading}
                >
                  {editLoading ? "Reloading..." : "Reload Product"}
                </button>
              ) : (
                <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                  Clear Form
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    );
  };

  const renderFetchWorkspace = () => {
    const suggestedCategory = toSafeString(productData?.suggestedCategory).trim();
    const previewSourceUrl = toSafeString(productData?.url || productUrl).trim();
    const previewImageCount = [
      toSafeString(productData?.image).trim(),
      ...(Array.isArray(productData?.images) ? productData.images : []),
    ].filter((image) => toSafeString(image).trim()).length;

    return (
      <div id="provider-product-url-import" className="provider-products-section-card">
        <div className="mb-4">
          <div className="provider-products-section-kicker">URL Fetch Workspace</div>
          <h5 className="mb-1">Add Product From URL</h5>
          <small className="text-muted">
            Paste a product page link, fetch the details, review them, then either save immediately or send them into the manual form.
          </small>
        </div>

        <div className="provider-products-helper-steps mb-4">
          <div className="provider-products-helper-step">
            <strong>1. Paste URL</strong>
            <span>Add the product page link you want to scrape.</span>
          </div>
          <div className="provider-products-helper-step">
            <strong>2. Review Details</strong>
            <span>Check text, price, and images before saving.</span>
          </div>
          <div className="provider-products-helper-step">
            <strong>3. Save or Refill</strong>
            <span>Save instantly or push data into the manual form.</span>
          </div>
        </div>

        <div className="row g-3 align-items-end">
          <div className="col-lg-8">
            <label className="form-label">Product Page Link</label>
            <input type="text" placeholder="Paste product page link here" className="form-control" value={productUrl} onChange={(e) => setProductUrl(e.target.value)} />
          </div>

          <div className="col-lg-4">
            <div className="d-grid gap-2">
              <button type="button" className="btn btn-primary" onClick={handleFetchProduct} disabled={fetchingProduct}>
                {fetchingProduct ? (
                  <CyclingStatusText active={fetchStepActive} steps={["Connecting", "Scraping", "Parsing", "Preparing"]} className="text-white" style={{ fontSize: 13, fontWeight: 600 }} />
                ) : (
                  "Fetch Details"
                )}
              </button>
              {productUrl.trim() ? (
                <a className="btn btn-outline-secondary" href={productUrl.trim()} target="_blank" rel="noreferrer">Open URL</a>
              ) : (
                <button type="button" className="btn btn-outline-secondary" disabled>Open URL</button>
              )}
            </div>
          </div>
        </div>

        {fetchError ? <div className="alert alert-danger mt-3 mb-0">{fetchError}</div> : null}

        {productData ? (
          <div className="provider-products-preview-card mt-4">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-4">
              <div>
                <div className="provider-products-section-kicker">Fetched Preview</div>
                <h5 className="mb-1">Scraped Product Details</h5>
                <small className="text-muted">
                  Review the scraped details before saving or sending them into the manual form.
                </small>
              </div>
              <div className="d-flex flex-wrap gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={applyScrapedData}>
                  {editingProductId ? "Refresh Edit Form" : "Use in Manual Form"}
                </button>
                <button type="button" className="btn btn-success" onClick={handleImportFromUrl} disabled={formSubmitting}>
                  {formSubmitting ? "Saving..." : "Save Product Now"}
                </button>
              </div>
            </div>

            <div className="provider-products-preview-summary">
              <div className="provider-products-preview-stat">
                <span className="provider-products-preview-stat-label">Price</span>
                <strong>{normalizePriceText(productData.price) ? formatMoney(Number(normalizePriceText(productData.price)), currency) : "Not Found"}</strong>
              </div>
              <div className="provider-products-preview-stat">
                <span className="provider-products-preview-stat-label">Category Hint</span>
                <strong>{suggestedCategory || "Unisex"}</strong>
              </div>
              <div className="provider-products-preview-stat">
                <span className="provider-products-preview-stat-label">Images</span>
                <strong>{previewImageCount || 0}</strong>
              </div>
              <div className="provider-products-preview-stat">
                <span className="provider-products-preview-stat-label">Source</span>
                {previewSourceUrl ? (
                  <a href={previewSourceUrl} target="_blank" rel="noreferrer" className="provider-products-preview-link">Open source page</a>
                ) : (
                  <strong>No source link</strong>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="form-group mb-3">
                  <label>Name</label>
                  <input type="text" className="form-control" value={toSafeString(productData.name)} onChange={(e) => setProductData({ ...productData, name: e.target.value })} />
                </div>

                <div className="form-group mb-3">
                  <label>Description</label>
                  <textarea className="form-control" rows="4" value={toSafeString(productData.description)} onChange={(e) => setProductData({ ...productData, description: e.target.value })} />
                </div>

                <div className="row">
                  <div className="col-md-4 mb-3">
                    <label>Price</label>
                    <input type="text" className="form-control" value={normalizePriceText(productData.price)} onChange={(e) => setProductData({ ...productData, price: normalizePriceText(e.target.value) })} />
                  </div>
                  <div className="col-md-8 mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1 gap-2 flex-wrap">
                      <label className="mb-0">Keywords</label>
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none"
                        onClick={() => {
                          setProductData((prev) => ({
                            ...prev,
                            keywords: createKeywordSuggestion(prev.name),
                          }));
                        }}
                      >
                        <i className="fa fa-magic me-1" />
                        Auto-generate
                      </button>
                    </div>
                    <input type="text" className="form-control" value={productData.keywords || ""} onChange={(e) => setProductData({ ...productData, keywords: e.target.value })} onKeyDown={(e) => handleKeywordsKeyDown(e, "preview")} placeholder="Comma or Enter to separate keywords" />
                  </div>
                </div>
              </div>
            </div>

            <ProductImagesManager
              primaryImage={toSafeString(productData.image)}
              extraImages={Array.isArray(productData.images) ? productData.images : []}
              onPrimaryChange={(value) =>
                setProductData((prev) =>
                  prev ? { ...prev, image: value, images: buildExtraImages(value, prev.images) } : prev
                )
              }
              onAddImage={addPreviewExtraImage}
              onExtraImageChange={updatePreviewExtraImage}
              onRemoveImage={removePreviewExtraImage}
              onMakePrimary={makePreviewImagePrimary}
              onUploadFiles={handlePreviewImageUpload}
              uploading={previewImageUploading}
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderBulkWorkspace = () => {
    const selectedCount = bulkItems.filter((row) => row.selected).length;
    const invalidCount = bulkItems.filter((row) => row.selected && !isBulkRowValid(row)).length;

    return (
      <div id="provider-product-bulk-import" className="provider-products-section-card">
        <div className="mb-4">
          <div className="provider-products-section-kicker">Bulk Workspace</div>
          <h5 className="mb-1">Bulk Import From Category Page</h5>
          <small className="text-muted">
            Paste the category page link, preview the products, adjust anything you need, and import only the selected rows.
          </small>
        </div>

        <div className="provider-products-helper-steps mb-4">
          <div className="provider-products-helper-step">
            <strong>1. Paste Category URL</strong>
            <span>Use the collection page you want to scrape.</span>
          </div>
          <div className="provider-products-helper-step">
            <strong>2. Review Preview Rows</strong>
            <span>Edit names, prices, images, and categories before import.</span>
          </div>
          <div className="provider-products-helper-step">
            <strong>3. Import Selected</strong>
            <span>Only checked and valid products will be imported.</span>
          </div>
        </div>

        <div className="row g-3 align-items-end">
          <div className="col-lg-7">
            <label className="form-label">Category Page Link</label>
            <input type="text" className="form-control" placeholder="Paste category page link here" value={bulkCollectionUrl} onChange={(e) => setBulkCollectionUrl(e.target.value)} disabled={bulkPreviewLoading || bulkImporting} />
          </div>

          <div className="col-lg-3">
            <label className="form-label">Category</label>
            <select className="form-control" value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)} disabled={bulkPreviewLoading || bulkImporting}>
              {BULK_CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {bulkCategory === "auto" && bulkDetectedCategory ? <small className="text-muted">Detected: {bulkDetectedCategory}</small> : null}
          </div>

          <div className="col-lg-2">
            <button type="button" className="btn btn-primary w-100" onClick={handleBulkPreview} disabled={bulkPreviewLoading || bulkImporting}>
              {bulkPreviewLoading ? (
                <CyclingStatusText active={bulkPreviewStepActive} steps={["Connecting", "Scanning", "Collecting", "Scraping", "Preparing"]} className="text-white" style={{ fontSize: 13, fontWeight: 600 }} />
              ) : (
                "Preview"
              )}
            </button>
          </div>
        </div>

        {bulkError ? <div className="alert alert-danger mt-3 mb-0">{bulkError}</div> : null}
        {bulkSuccess ? <div className="alert alert-success mt-3 mb-0">{bulkSuccess}</div> : null}

        {bulkItems.length > 0 ? (
          <div className="mt-4">
            <div className="provider-products-overview-strip mb-3">
              <div className="provider-products-overview-card">
                <span className="provider-products-stat-label">Preview Rows</span>
                <strong>{bulkItems.length}</strong>
                <small>Total products found in this preview.</small>
              </div>
              <div className="provider-products-overview-card">
                <span className="provider-products-stat-label">Selected</span>
                <strong>{selectedCount}</strong>
                <small>These rows will be imported when valid.</small>
              </div>
              <div className="provider-products-overview-card">
                <span className="provider-products-stat-label">Needs Fixes</span>
                <strong>{invalidCount}</strong>
                <small>Highlighted rows need name, image, and price.</small>
              </div>
            </div>

            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3 mb-3">
              <div className="provider-products-bulk-note">Rows highlighted in amber need fixes before import.</div>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setBulkItems([]);
                    setBulkError("");
                    setBulkSuccess("");
                  }}
                  disabled={bulkImporting || bulkPreviewLoading}
                >
                  Clear Preview
                </button>
                <button type="button" className="btn btn-success" onClick={handleBulkImport} disabled={bulkImporting || bulkPreviewLoading}>
                  {bulkImporting ? "Importing..." : "Import Selected"}
                </button>
              </div>
            </div>

            <div className="table-responsive provider-products-bulk-table">
              <table className="table table-striped align-middle mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 50 }}>
                      <input type="checkbox" checked={bulkItems.length > 0 && bulkItems.every((row) => row.selected)} onChange={toggleBulkSelectAll} />
                    </th>
                    <th style={{ minWidth: 260 }}>Name</th>
                    <th style={{ width: 140 }}>Price</th>
                    <th style={{ minWidth: 320 }}>Image</th>
                    <th style={{ width: 140 }}>Category</th>
                    <th style={{ minWidth: 280 }}>Source URL</th>
                    <th style={{ width: 110 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkItems.map((row) => {
                    const rowValid = isBulkRowValid(row);
                    return (
                      <tr key={row.tempId} style={row.selected && !rowValid ? { backgroundColor: "#fff3cd" } : undefined}>
                        <td>
                          <input type="checkbox" checked={!!row.selected} onChange={() => toggleBulkRowSelected(row.tempId)} />
                        </td>
                        <td>
                          <input type="text" className="form-control" value={toSafeString(row.product_name)} onChange={(e) => handleBulkRowChange(row.tempId, "product_name", e.target.value)} style={{ minWidth: 240 }} />
                        </td>
                        <td>
                          <input type="number" className="form-control" value={toSafeString(row.product_price)} onChange={(e) => handleBulkRowChange(row.tempId, "product_price", e.target.value)} min={0} step="0.01" />
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div style={{ width: 54 }}>
                              {row.product_image ? (
                                <img src={row.product_image} alt="preview" style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: "1px solid #eaeaea" }} />
                              ) : (
                                <div style={{ width: 54, height: 54, borderRadius: 8, border: "1px dashed #ccc" }} />
                              )}
                            </div>
                            <input type="text" className="form-control" value={toSafeString(row.product_image)} onChange={(e) => handleBulkRowChange(row.tempId, "product_image", e.target.value)} style={{ minWidth: 240 }} />
                          </div>
                        </td>
                        <td>
                          <select className="form-control" value={row.category || bulkCategory} onChange={(e) => handleBulkRowChange(row.tempId, "category", e.target.value)}>
                            {CATEGORY_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          {row.source_url ? (
                            <a href={row.source_url} target="_blank" rel="noreferrer" className="provider-products-link-ellipsis" title={row.source_url}>
                              {row.source_url}
                            </a>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeBulkRow(row.tempId)} disabled={bulkImporting || bulkPreviewLoading}>
                            Remove
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderWorkspaceShortcuts = (activeView) => (
    <div className="provider-products-toolbar">
      <Link
        to={workspacePaths.list}
        className={`btn btn-sm ${
          activeView === "list" ? "btn-dark" : "btn-outline-secondary"
        }`}
      >
        Products
      </Link>
      <Link
        to={workspacePaths.new}
        className={`btn btn-sm ${
          activeView === "new" ? "btn-dark" : "btn-outline-secondary"
        }`}
      >
        New Product
      </Link>
      <Link
        to={workspacePaths.fetch}
        className={`btn btn-sm ${
          activeView === "fetch" ? "btn-dark" : "btn-outline-secondary"
        }`}
      >
        Fetch URL
      </Link>
      <Link
        to={workspacePaths.import}
        className={`btn btn-sm ${
          activeView === "import" ? "btn-dark" : "btn-outline-secondary"
        }`}
      >
        Bulk Import
      </Link>
    </div>
  );

  const renderWorkspacePage = (content) => (
    <div className="card border-0 shadow-sm provider-products-surface">
      <div className="card-header bg-white border-0 pt-4 px-4 pb-3">
        <div className="d-flex flex-column flex-xl-row justify-content-between align-items-xl-start gap-3">
          <div>
            <div className="provider-products-section-kicker">
              {currentViewMeta.kicker}
            </div>
            <h4 className="mb-1">{currentViewMeta.title}</h4>
            <small className="text-muted">{currentViewMeta.subtitle}</small>
          </div>

          <div className="provider-products-toolbar provider-products-toolbar-tight">
            <Link to={workspacePaths.list} className="btn btn-outline-primary btn-sm">
              <i className="fa fa-arrow-left me-1" />
              Back to Products
            </Link>
            {renderWorkspaceShortcuts(currentView)}
          </div>
        </div>
      </div>

      <div className="card-body p-4">
        {content}
      </div>
    </div>
  );

  const renderCurrentPage = () => {
    if (currentView === "list") {
      return renderProductsTable();
    }

    if (currentView === "fetch") {
      return renderWorkspacePage(
        <>
          {formSuccess ? <div className="alert alert-success mb-4">{formSuccess}</div> : null}
          {renderFetchWorkspace()}
        </>
      );
    }

    if (currentView === "import") {
      return renderWorkspacePage(renderBulkWorkspace());
    }

    if (currentView === "edit") {
      return renderWorkspacePage(
        <>
          {editLoadError ? <div className="alert alert-danger mb-4">{editLoadError}</div> : null}
          {editLoading ? (
            <div className="provider-products-loading-card">
              Loading product details...
            </div>
          ) : !editLoadError ? (
            renderManualWorkspace()
          ) : null}
        </>
      );
    }

    return renderWorkspacePage(renderManualWorkspace());
  };

  return (
    <>
      <Sidebar isSidebarOpen={isSidebarOpen} />

      <div
        className="content-overlay"
        onClick={() =>
          isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()
        }
      >
        <div
          className="content-wrapper"
          style={{
            marginLeft: isSidebarOpen ? "250px" : "70px",
            transition: "margin-left 0.3s",
          }}
        >
          <Header toggleSidebar={toggleSidebar} />

          <section className="content provider-products-page">
            <div className="breadcrumb-main-bx">
              <div className="breadcrumb-bx">
                <Link className="breadcrumb-link" to="/admin/providers">
                  Providers
                </Link>
                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                <Link className="breadcrumb-link" to={workspacePaths.list}>
                  Provider Products
                </Link>
                {currentView !== "list" ? (
                  <>
                    <div className="breadcrumb-link breadcrumb-disabled">/</div>
                    <div className="breadcrumb-link breadcrumb-active">
                      {currentViewMeta.title}
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            <div className="container-fluid">
              {renderCurrentPage()}
            </div>
          </section>

          {false && (
          <section className="content">
            <div className="breadcrumb-main-bx">
              <div className="breadcrumb-bx">
                <Link className="breadcrumb-link" to="/admin/providers">
                  Providers
                </Link>
                <div className="breadcrumb-link breadcrumb-disabled">/</div>
                <div className="breadcrumb-link breadcrumb-active">
                  Provider Products
                </div>
              </div>
            </div>

            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <h4 style={{ margin: 0 }}>Provider Products</h4>
                        <small className="text-muted">
                          {providerName ? providerName : "Provider"} · ID #{id}
                        </small>
                      </div>

                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          className="btn btn-primary"
                          onClick={() =>
                            document
                              .getElementById("provider-product-bulk-import")
                              ?.scrollIntoView({ behavior: "smooth" })
                          }
                        >
                          📦 Bulk Import
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={() =>
                            document
                              .getElementById("provider-product-url-import")
                              ?.scrollIntoView({ behavior: "smooth" })
                          }
                        >
                          � Import by URL
                        </button>
                        <button
                          className="btn btn-success"
                          onClick={() =>
                            document
                              .getElementById("provider-product-form")
                              ?.scrollIntoView({ behavior: "smooth" })
                          }
                        >
                          ➕ Add Manually
                        </button>
                      </div>
                    </div>

                    <div className="card-body">
                      <div
                        id="provider-product-bulk-import"
                        className="card"
                        style={{ padding: "20px" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5 className="mb-0">Bulk Import (Category Page)</h5>
                            <small className="text-muted">
                              Paste the category page link. Preview products, edit if needed, then import.
                            </small>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-7 mb-3">
                            <label className="form-label">Category Page Link</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Paste category page link here"
                              value={bulkCollectionUrl}
                              onChange={(e) => setBulkCollectionUrl(e.target.value)}
                              disabled={bulkPreviewLoading || bulkImporting}
                            />
                          </div>

                          <div className="col-md-3 mb-3">
                            <label className="form-label">Category</label>
                            <select
                              className="form-control"
                              value={bulkCategory}
                              onChange={(e) => setBulkCategory(e.target.value)}
                              disabled={bulkPreviewLoading || bulkImporting}
                            >
                              {BULK_CATEGORY_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            {bulkCategory === "auto" && bulkDetectedCategory && (
                              <small className="text-muted">
                                Detected: {bulkDetectedCategory}
                              </small>
                            )}
                          </div>

                          <div className="col-md-2 d-flex align-items-end mb-3">
                            <button
                              className="btn btn-primary w-100"
                              onClick={handleBulkPreview}
                              disabled={bulkPreviewLoading || bulkImporting}
                              style={{ minHeight: 38 }}
                            >
                              {bulkPreviewLoading ? (
                                <CyclingStatusText
                                  active={bulkPreviewStepActive}
                                  steps={[
                                    "Connecting",
                                    "Scanning",
                                    "Collecting",
                                    "Scraping",
                                    "Preparing",
                                  ]}
                                  className="text-white"
                                  style={{ fontSize: 13, fontWeight: 600 }}
                                />
                              ) : (
                                "Preview"
                              )}
                            </button>
                          </div>
                        </div>

                        {bulkError && (
                          <div className="alert alert-danger">{bulkError}</div>
                        )}
                        {bulkSuccess && (
                          <div className="alert alert-success">{bulkSuccess}</div>
                        )}

                        {bulkItems.length > 0 && (
                          <>
                            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                              <div className="text-muted">
                                Preview Items: <strong>{bulkItems.length}</strong>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                <button
                                  className="btn btn-outline-secondary"
                                  onClick={() => {
                                    setBulkItems([]);
                                    setBulkError("");
                                    setBulkSuccess("");
                                  }}
                                  disabled={bulkImporting || bulkPreviewLoading}
                                >
                                  Clear Preview
                                </button>
                                <button
                                  className="btn btn-success"
                                  onClick={handleBulkImport}
                                  disabled={bulkImporting || bulkPreviewLoading}
                                >
                                  {bulkImporting ? "Importing..." : "Import Selected"}
                                </button>
                              </div>
                            </div>

                            <div
                              className="table-responsive"
                              style={{ maxHeight: 420, overflow: "auto" }}
                            >
                              <table className="table table-striped mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: 50 }}>
                                      <input
                                        type="checkbox"
                                        checked={
                                          bulkItems.length > 0 &&
                                          bulkItems.every((row) => row.selected)
                                        }
                                        onChange={toggleBulkSelectAll}
                                      />
                                    </th>
                                    <th style={{ minWidth: 260 }}>Name</th>
                                    <th style={{ width: 140 }}>Price</th>
                                    <th style={{ minWidth: 320 }}>Image</th>
                                    <th style={{ width: 140 }}>Category</th>
                                    <th style={{ minWidth: 280 }}>Source URL</th>
                                    <th style={{ width: 110 }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {bulkItems.map((row) => {
                                    const rowValid = isBulkRowValid(row);
                                    return (
                                      <tr
                                        key={row.tempId}
                                        style={
                                          row.selected && !rowValid
                                            ? { backgroundColor: "#fff3cd" }
                                            : undefined
                                        }
                                      >
                                        <td>
                                          <input
                                            type="checkbox"
                                            checked={!!row.selected}
                                            onChange={() =>
                                              toggleBulkRowSelected(row.tempId)
                                            }
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="text"
                                            className="form-control"
                                            value={toSafeString(row.product_name)}
                                            onChange={(e) =>
                                              handleBulkRowChange(
                                                row.tempId,
                                                "product_name",
                                                e.target.value
                                              )
                                            }
                                            style={{ minWidth: 240 }}
                                          />
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control"
                                            value={toSafeString(row.product_price)}
                                            onChange={(e) =>
                                              handleBulkRowChange(
                                                row.tempId,
                                                "product_price",
                                                e.target.value
                                              )
                                            }
                                            min={0}
                                            step="0.01"
                                          />
                                        </td>
                                        <td>
                                          <div className="d-flex align-items-center gap-2">
                                            <div style={{ width: 54 }}>
                                              {row.product_image ? (
                                                <img
                                                  src={row.product_image}
                                                  alt="preview"
                                                  style={{
                                                    width: 54,
                                                    height: 54,
                                                    objectFit: "cover",
                                                    borderRadius: 8,
                                                    border: "1px solid #eaeaea",
                                                  }}
                                                />
                                              ) : (
                                                <div
                                                  style={{
                                                    width: 54,
                                                    height: 54,
                                                    borderRadius: 8,
                                                    border: "1px dashed #ccc",
                                                  }}
                                                />
                                              )}
                                            </div>
                                            <input
                                              type="text"
                                              className="form-control"
                                              value={toSafeString(row.product_image)}
                                              onChange={(e) =>
                                                handleBulkRowChange(
                                                  row.tempId,
                                                  "product_image",
                                                  e.target.value
                                                )
                                              }
                                              style={{ minWidth: 240 }}
                                            />
                                          </div>
                                        </td>
                                        <td>
                                          <select
                                            className="form-control"
                                            value={row.category || bulkCategory}
                                            onChange={(e) =>
                                              handleBulkRowChange(
                                                row.tempId,
                                                "category",
                                                e.target.value
                                              )
                                            }
                                          >
                                            {CATEGORY_OPTIONS.map((opt) => (
                                              <option
                                                key={opt.value}
                                                value={opt.value}
                                              >
                                                {opt.label}
                                              </option>
                                            ))}
                                          </select>
                                        </td>
                                        <td>
                                          {row.source_url ? (
                                            <a
                                              href={row.source_url}
                                              target="_blank"
                                              rel="noreferrer"
                                              style={{
                                                display: "inline-block",
                                                maxWidth: 360,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                verticalAlign: "bottom",
                                              }}
                                              title={row.source_url}
                                            >
                                              {row.source_url}
                                            </a>
                                          ) : (
                                            <span className="text-muted">-</span>
                                          )}
                                        </td>
                                        <td>
                                          <button
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={() => removeBulkRow(row.tempId)}
                                            disabled={bulkImporting || bulkPreviewLoading}
                                          >
                                            Remove
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </>
                        )}
                      </div>

                      <div
                        style={{
                          borderTop: "1px solid #eee",
                          marginTop: 22,
                          paddingTop: 22,
                        }}
                      />

                      <div
                        id="provider-product-url-import"
                        className="card"
                        style={{ padding: "18px" }}
                      >
                        <h5 className="mb-1">Add Product (Product Page)</h5>
                        <small className="text-muted">
                          Paste the product page link. We’ll fetch details, then you can edit and save.
                        </small>

                        <div
                          style={{ display: "flex", gap: "10px", marginTop: "10px" }}
                        >
                          <input
                            type="text"
                            placeholder="Paste product page link here"
                            className="form-control"
                            value={productUrl}
                            onChange={(e) => setProductUrl(e.target.value)}
                          />

                          <button
                            className="btn btn-primary"
                            onClick={handleFetchProduct}
                            disabled={fetchingProduct}
                            style={{ minWidth: 140 }}
                          >
                            {fetchingProduct ? (
                              <CyclingStatusText
                                active={fetchStepActive}
                                steps={[
                                  "Connecting",
                                  "Scraping",
                                  "Parsing",
                                  "Preparing",
                                ]}
                                className="text-white"
                                style={{ fontSize: 13, fontWeight: 600 }}
                              />
                            ) : (
                              "Fetch"
                            )}
                          </button>
                        </div>

                        {fetchError && (
                          <p style={{ color: "red", marginTop: "10px" }}>
                            {fetchError}
                          </p>
                        )}

                        {productData && (
                          <div
                            className="card"
                            style={{
                              padding: "20px",
                              marginTop: "15px",
                              borderRadius: "8px",
                            }}
                          >
                            <h5 style={{ marginBottom: "15px" }}>
                              Product Details
                            </h5>

                            <div className="row">
                              <div className="col-12">
                                <div className="form-group mb-3">
                                  <label>Name</label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={productData.name}
                                    onChange={(e) =>
                                      setProductData({
                                        ...productData,
                                        name: e.target.value,
                                      })
                                    }
                                  />
                                </div>

                                <div className="form-group mb-3">
                                  <label>Description</label>
                                  <textarea
                                    className="form-control"
                                    rows="4"
                                    value={productData.description}
                                    onChange={(e) =>
                                      setProductData({
                                        ...productData,
                                        description: e.target.value,
                                      })
                                    }
                                  />
                                </div>

                                <div className="row">
                                  <div className="col-md-4 mb-3">
                                    <label>Price</label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={normalizePriceText(productData.price)}
                                      onChange={(e) =>
                                        setProductData({
                                          ...productData,
                                          price: normalizePriceText(e.target.value),
                                        })
                                      }
                                    />
                                  </div>
                                  <div className="col-md-8 mb-3">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <label className="mb-0">Keywords</label>
                                      <button
                                        className="btn btn-link btn-sm p-0 text-decoration-none"
                                        onClick={() => {
                                          const noise = new Set(["and", "the", "for", "with", "from", "your", "this", "that", "made", "high", "quality", "medical", "scrubs", "scrub", "top", "pant", "suit"]);
                                          const words = toSafeString(productData.name).toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter(w => w.length > 2 && !noise.has(w));
                                          const unique = Array.from(new Set(words));
                                          setProductData(prev => ({ ...prev, keywords: unique.join(", ") }));
                                        }}
                                      >
                                        <i className="fa fa-magic me-1"></i>Auto-generate
                                      </button>
                                    </div>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={productData.keywords || ""}
                                      onChange={(e) =>
                                        setProductData({
                                          ...productData,
                                          keywords: e.target.value,
                                        })
                                      }
                                      onKeyDown={(e) => handleKeywordsKeyDown(e, "preview")}
                                      placeholder="Comma or Enter to separate keywords"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <ProductImagesManager
                              primaryImage={toSafeString(productData.image)}
                              extraImages={Array.isArray(productData.images) ? productData.images : []}
                              onPrimaryChange={(value) =>
                                setProductData((prev) =>
                                  prev
                                    ? {
                                        ...prev,
                                        image: value,
                                        images: buildExtraImages(value, prev.images),
                                      }
                                    : prev
                                )
                              }
                              onAddImage={addPreviewExtraImage}
                              onExtraImageChange={updatePreviewExtraImage}
                              onRemoveImage={removePreviewExtraImage}
                              onMakePrimary={makePreviewImagePrimary}
                              onUploadFiles={handlePreviewImageUpload}
                              uploading={previewImageUploading}
                            />

                            <div className="d-flex flex-wrap gap-2 mt-3">
                              <button
                                className="btn btn-outline-secondary"
                                style={{ minWidth: 220 }}
                                onClick={applyScrapedData}
                              >
                                ✨ Use These Details in Form
                              </button>
                              <button
                                className="btn btn-success"
                                style={{ minWidth: 220 }}
                                onClick={handleImportFromUrl}
                                disabled={formSubmitting}
                              >
                                {formSubmitting ? "Saving..." : "Save Product Now"}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>

                      <div
                        style={{
                          borderTop: "1px solid #eee",
                          marginTop: 22,
                          paddingTop: 22,
                        }}
                      />

                      <div
                        id="provider-product-form"
                        className="card"
                        style={{ padding: "20px", marginTop: "20px" }}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5 className="mb-0">
                              {editingProductId ? "Edit Product" : "Add New Product"}
                            </h5>
                            <small className="text-muted">
                              Fill out the details below to
                              {editingProductId ? " update" : " create"} a product.
                            </small>
                          </div>
                          {editingProductId && (
                            <button className="btn btn-outline-secondary" onClick={resetForm}>
                              Cancel Edit
                            </button>
                          )}
                        </div>

                        <form onSubmit={handleFormSubmit}>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Product Name</label>
                              <input
                                type="text"
                                className={`form-control ${
                                  formErrors.product_name ? "is-invalid" : ""
                                }`}
                                name="product_name"
                                value={formData.product_name}
                                onChange={handleFormChange}
                              />
                              {formErrors.product_name && (
                                <div className="invalid-feedback">
                                  {formErrors.product_name}
                                </div>
                              )}
                            </div>

                            <div className="col-md-3 mb-3">
                              <label className="form-label">Category</label>
                              <select
                                className={`form-control ${
                                  formErrors.category ? "is-invalid" : ""
                                }`}
                                name="category"
                                value={formData.category}
                                onChange={handleFormChange}
                              >
                                {CATEGORY_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                              {formErrors.category && (
                                <div className="invalid-feedback">
                                  {formErrors.category}
                                </div>
                              )}
                            </div>

                            <div className="col-md-3 mb-3">
                              <label className="form-label">Price ({currency.code})</label>
                              <input
                                type="text"
                                className={`form-control ${
                                  formErrors.product_price ? "is-invalid" : ""
                                }`}
                                name="product_price"
                                value={formData.product_price}
                                onChange={handleFormChange}
                              />
                              {formErrors.product_price && (
                                <div className="invalid-feedback">
                                  {formErrors.product_price}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <label className="form-label">Meta Description (Product Description)</label>
                              <textarea
                                className="form-control"
                                rows="3"
                                name="description"
                                value={formData.description || ""}
                                onChange={handleFormChange}
                                placeholder="Enter a clear product description. This will be used as the meta description and shown on the product page."
                              />
                              <small className="text-muted">Recommended: 120–160 characters. Shown as meta description for SEO and as the product description on the frontend.</small>
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <label className="form-label mb-0">Meta Keywords</label>
                                <button 
                                  className="btn btn-link btn-sm p-0 text-decoration-none"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    const noise = new Set(["and", "the", "for", "with", "from", "your", "this", "that", "made", "high", "quality", "medical", "scrubs", "scrub", "top", "pant", "suit"]);
                                    const words = toSafeString(formData.product_name).toLowerCase().replace(/[^a-z0-9\s]/g, "").split(/\s+/).filter(w => w.length > 2 && !noise.has(w));
                                    const unique = Array.from(new Set(words));
                                    setFormData(prev => ({ ...prev, keywords: unique.join(", ") }));
                                  }}
                                >
                                  <i className="fa fa-magic me-1"></i>Auto-generate
                                </button>
                              </div>
                              <input
                                type="text"
                                className="form-control"
                                name="keywords"
                                value={formData.keywords || ""}
                                onChange={handleFormChange}
                                onKeyDown={(e) => handleKeywordsKeyDown(e, "form")}
                                placeholder="e.g. medical scrubs, nursing uniform (Press Enter or Comma to separate)"
                              />
                              <small className="text-muted">Keywords are used as meta keywords and image alt text. Press Enter or Comma after each keyword.</small>
                            </div>
                          </div>

                          <div className="mb-3">
                            <ProductImagesManager
                              primaryImage={toSafeString(formData.product_image)}
                              extraImages={Array.isArray(formData.images) ? formData.images : []}
                              errors={formErrors}
                              onPrimaryChange={(value) => {
                                setFormErrors((prev) => ({
                                  ...prev,
                                  product_image: undefined,
                                  images: undefined,
                                }));
                                setFormData((prev) => ({
                                  ...prev,
                                  product_image: value,
                                  images: buildExtraImages(value, prev.images),
                                }));
                              }}
                              onAddImage={addFormExtraImage}
                              onExtraImageChange={updateFormExtraImage}
                              onRemoveImage={removeFormExtraImage}
                              onMakePrimary={makeFormImagePrimary}
                              onUploadFiles={handleFormImageUpload}
                              uploading={formImageUploading}
                            />
                            {formErrors.images && (
                              <div className="text-danger small mt-2">{formErrors.images}</div>
                            )}
                          </div>

                          {formErrors.api && (
                            <div className="alert alert-danger">{formErrors.api}</div>
                          )}
                          {formSuccess && (
                            <div className="alert alert-success">{formSuccess}</div>
                          )}

                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={formSubmitting}
                          >
                            {formSubmitting
                              ? "Saving..."
                              : editingProductId
                              ? "Update Product"
                              : "Create Product"}
                          </button>
                        </form>
                      </div>

                      <div className="card mt-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h5 className="mb-0">
                            Products ({productsTotal || products.length})
                          </h5>
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={fetchProducts}
                            disabled={tableLoading}
                          >
                            {tableLoading ? "Refreshing..." : "Refresh"}
                          </button>
                        </div>

                        <div className="card-body p-0">
                          {tableError && (
                            <div className="alert alert-danger m-3">{tableError}</div>
                          )}

                          <div className="table-responsive">
                            <table className="table table-striped mb-0">
                              <thead>
                                <tr>
                                  <th style={{ minWidth: 250 }}>Product</th>
                                  <th>Category</th>
                                  <th>Price</th>
                                  <th>Created</th>
                                  <th style={{ width: 140 }}>Actions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {tableLoading ? (
                                  <tr>
                                    <td colSpan={5} className="text-center py-4">
                                      Loading products...
                                    </td>
                                  </tr>
                                ) : products.length ? (
                                  products.map((product) => (
                                    <tr key={product._id}>
                                      <td>
                                        <div className="d-flex align-items-center gap-3">
                                          {product.product_image ? (
                                            <img
                                              src={buildImageUrl(product.product_image)}
                                              alt={product.product_name}
                                              loading="lazy"
                                              style={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 8,
                                                objectFit: "cover",
                                                border: "1px solid #eaeaea",
                                              }}
                                            />
                                          ) : (
                                            <div
                                              style={{
                                                width: 60,
                                                height: 60,
                                                borderRadius: 8,
                                                border: "1px dashed #ccc",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#888",
                                              }}
                                            >
                                              N/A
                                            </div>
                                          )}
                                          <div>
                                            <strong>{product.product_name}</strong>
                                            <div className="text-muted small">
                                              #{product._id.slice(-6)}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      <td className="text-capitalize">
                                        {product.category}
                                      </td>
                                      <td>{formatMoney(product.product_price, currency)}</td>
                                      <td>
                                        {new Date(product.createdAt).toLocaleDateString()}
                                      </td>
                                      <td>
                                        <button
                                          className="btn btn-sm btn-outline-secondary me-2"
                                          onClick={() => handleEdit(product)}
                                        >
                                          Edit
                                        </button>
                                        <button
                                          className="btn btn-sm btn-outline-danger"
                                          onClick={() => handleDelete(product)}
                                        >
                                          Delete
                                        </button>
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={5} className="text-center py-4 text-muted">
                                      No products found.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          <div ref={loadMoreRef} style={{ height: 1 }} />

                          {productsHasMore && !tableLoading && (
                            <div className="text-center py-3 text-muted">
                              {loadingMoreProducts ? "Loading more..." : "Scroll to load more"}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          )}

          {pendingDeleteProduct ? (
            <div
              className="provider-products-delete-modal-backdrop"
              onClick={handleCancelDelete}
            >
              <div
                className="provider-products-delete-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="provider-product-delete-title"
                onClick={(event) => event.stopPropagation()}
              >
                <div className="provider-products-delete-modal-header">
                  <button
                    type="button"
                    className="provider-products-delete-close"
                    onClick={handleCancelDelete}
                    disabled={deleteSubmitting}
                    aria-label="Close delete dialog"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>

                <div className="provider-products-delete-icon">
                  <svg
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="provider-products-delete-icon-svg"
                  >
                    <path
                      d="M9 3.75h6a1.5 1.5 0 0 1 1.5 1.5V6H21a.75.75 0 0 1 0 1.5h-1.03l-.82 10.57A2.25 2.25 0 0 1 16.91 20.25H7.09a2.25 2.25 0 0 1-2.24-2.18L4.03 7.5H3a.75.75 0 0 1 0-1.5h4.5v-.75A1.5 1.5 0 0 1 9 3.75Zm6 2.25v-.75H9V6h6Zm-8.62 1.5.8 10.45a.75.75 0 0 0 .75.72h8.14a.75.75 0 0 0 .75-.72l.8-10.45H6.38Zm2.87 2.25a.75.75 0 0 1 .75.75v5.25a.75.75 0 0 1-1.5 0V10.5a.75.75 0 0 1 .75-.75Zm5.25 0a.75.75 0 0 1 .75.75v5.25a.75.75 0 0 1-1.5 0V10.5a.75.75 0 0 1 .75-.75Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>

                <div className="provider-products-delete-copy">
                  <h5
                    id="provider-product-delete-title"
                    className="mb-2"
                  >
                    Delete Product
                  </h5>
                  <p className="provider-products-delete-message mb-1">
                    Are you sure you want to delete this product?
                  </p>
                  <p className="provider-products-delete-name mb-0">
                    {pendingDeleteProduct.product_name}
                  </p>
                </div>

                <div className="provider-products-delete-modal-body">
                  {deleteError ? (
                    <div className="alert alert-danger mb-0">
                      {deleteError}
                    </div>
                  ) : null}
                </div>

                <div className="provider-products-delete-modal-footer">
                  <button
                    type="button"
                    className="btn provider-products-delete-cancel"
                    onClick={handleCancelDelete}
                    disabled={deleteSubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn provider-products-delete-confirm"
                    onClick={handleConfirmDelete}
                    disabled={deleteSubmitting}
                  >
                    {deleteSubmitting ? "Deleting..." : "Confirm"}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          <Footer />
        </div>
      </div>
    </>
  );
};

export default ProviderProducts;

